// src/seo/SEO.js
import { useEffect } from "react";

/**
 * Adds or updates a meta/link/script tag in <head>.
 */
function upsertTag({ selector, tag, attrs, innerText }) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs || {}).forEach(([k, v]) => {
    if (v === null || v === undefined) el.removeAttribute(k);
    else el.setAttribute(k, String(v));
  });
  if (innerText !== undefined) el.textContent = innerText;
  return el;
}

/**
 * Removes tag if exists.
 */
function removeTag(selector) {
  const el = document.head.querySelector(selector);
  if (el) el.remove();
}

/**
 * useSEO: lightweight hook to manage SEO tags without external deps.
 */
export function useSEO({
  title,
  description,
  keywords = [],
  canonical,
  robots = "index,follow",
  // Open Graph / Twitter
  og = {},
  twitter = {},
} = {}) {
  useEffect(() => {
    // Title
    if (title) document.title = title;

    // Meta: description
    if (description) {
      upsertTag({
        selector: 'meta[name="description"]',
        tag: "meta",
        attrs: { name: "description", content: description },
      });
    } else {
      removeTag('meta[name="description"]');
    }

    // Meta: keywords
    const kw = (keywords || []).join(", ");
    if (kw) {
      upsertTag({
        selector: 'meta[name="keywords"]',
        tag: "meta",
        attrs: { name: "keywords", content: kw },
      });
    } else {
      removeTag('meta[name="keywords"]');
    }

    // Meta: robots
    upsertTag({
      selector: 'meta[name="robots"]',
      tag: "meta",
      attrs: { name: "robots", content: robots || "index,follow" },
    });

    // Theme color
    upsertTag({
      selector: 'meta[name="theme-color"]',
      tag: "meta",
      attrs: { name: "theme-color", content: "#0B0E11" },
    });

    // Canonical
    if (canonical) {
      upsertTag({
        selector: 'link[rel="canonical"]',
        tag: "link",
        attrs: { rel: "canonical", href: canonical },
      });
    } else {
      removeTag('link[rel="canonical"]');
    }

    // ----- Open Graph -----
    const ogMap = {
      "og:type": og.type || "website",
      "og:title": og.title || title,
      "og:description": og.description || description,
      "og:url": og.url || canonical,
      "og:image": og.image,
      "og:site_name": og.siteName || "1CryptoX",
    };
    Object.entries(ogMap).forEach(([property, content]) => {
      if (!content) return;
      upsertTag({
        selector: `meta[property="${property}"]`,
        tag: "meta",
        attrs: { property, content },
      });
    });

    // ----- Twitter Cards -----
    const twMap = {
      "twitter:card": twitter.card || "summary_large_image",
      "twitter:title": twitter.title || title,
      "twitter:description": twitter.description || description,
      "twitter:image": twitter.image || og.image,
    };
    Object.entries(twMap).forEach(([name, content]) => {
      if (!content) return;
      upsertTag({
        selector: `meta[name="${name}"]`,
        tag: "meta",
        attrs: { name, content },
      });
    });

    // Cleanup on unmount? Keep tags until next route.
    return () => {};
  }, [
    title,
    description,
    JSON.stringify(keywords),
    canonical,
    robots,
    JSON.stringify(og),
    JSON.stringify(twitter),
  ]);
}

/**
 * Injects/updates a single JSON-LD script in head. You can call multiple times with different ids.
 */
export function useJsonLD(id, data) {
  useEffect(() => {
    if (!data) {
      if (id) removeTag(`script[type="application/ld+json"][data-id="${id}"]`);
      return;
    }
    upsertTag({
      selector: `script[type="application/ld+json"][data-id="${id}"]`,
      tag: "script",
      attrs: { type: "application/ld+json", "data-id": id },
      innerText: JSON.stringify(data),
    });
  }, [id, JSON.stringify(data)]);
}
